import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  BlogKnowledgeInput,
  BlogKnowledgeItem,
  deleteAdminBlogKnowledge,
  createAdminCloudflareR2Upload,
  createAdminCloudflareStreamUpload,
  listAdminBlogKnowledge,
  saveAdminBlogKnowledge,
} from "./firebaseFunctions";
import { useUser } from "../contexts/UserContext";
import styles from "./AdminKnowledgePage.module.css";

const MAX_VIDEO_FILE_SIZE = 200 * 1024 * 1024;
const MAX_IMAGE_FILE_SIZE = 20 * 1024 * 1024;
const ALLOWED_FILE_TYPES = /^(image|video)\//;
const KNOWLEDGE_PAGE_SIZE = 30;

const emptyForm: BlogKnowledgeInput = {
  title: "",
  description: "",
  tags: [],
  sourceLabel: "",
  sourceUrl: "",
  materialType: "information",
  mediaPath: "",
  mediaName: "",
  mediaContentType: "",
  streamUid: "",
  streamUrl: "",
  approvedForBlog: true,
  publicMedia: true,
};

const AdminKnowledgePage: React.FC = () => {
  const userContext = useUser();
  const userDetails = userContext?.userDetails;
  const isAdmin =
    userDetails?.id !== "Guest" &&
    typeof userDetails?.userLevel === "number" &&
    userDetails.userLevel <= 1;
  const [items, setItems] = useState<BlogKnowledgeItem[]>([]);
  const [form, setForm] = useState<BlogKnowledgeInput>(emptyForm);
  const [tagInput, setTagInput] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [appliedImageUrl, setAppliedImageUrl] = useState("");
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [isApplyingImageUrl, setIsApplyingImageUrl] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState<Array<string | null>>([null]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<BlogKnowledgeItem | null>(null);

  const loadItemsPage = useCallback(async (page: number, cursor: string | null) => {
    if (!isAdmin) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await listAdminBlogKnowledge(KNOWLEDGE_PAGE_SIZE, cursor);
      setItems(response.items);
      setHasMore(response.hasMore);
      setPageIndex(page);
      setPageCursors((current) => {
        const next = current.slice(0, Math.max(current.length, page + 1));
        next[page] = cursor;
        if (response.hasMore && response.nextCursor) {
          next[page + 1] = response.nextCursor;
        } else {
          next.splice(page + 1);
        }
        next[0] = null;
        return next;
      });
    } catch (loadError: any) {
      setError(loadError?.message ?? "지식 자료를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      void loadItemsPage(0, null);
    }
  }, [isAdmin, loadItemsPage]);

  useEffect(() => {
    if (!file || !file.type.startsWith("image/")) {
      setLocalPreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const startNew = () => {
    setForm(emptyForm);
    setTagInput("");
    setFile(null);
    setImageUrl("");
    setAppliedImageUrl("");
    setUploadProgress(0);
    setStatus("");
    setError("");
  };

  const selectItem = (item: BlogKnowledgeItem) => {
    setForm({ ...item });
    setTagInput(item.tags.join(", "));
    setFile(null);
    setImageUrl("");
    setAppliedImageUrl("");
    setUploadProgress(0);
    setStatus("");
    setError("");
  };

  const selectLocalFile = (selectedFile: File | null) => {
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const fileSizeLimit = selectedFile.type.startsWith("image/")
      ? MAX_IMAGE_FILE_SIZE
      : MAX_VIDEO_FILE_SIZE;
    if (!ALLOWED_FILE_TYPES.test(selectedFile.type) || selectedFile.size <= 0 || selectedFile.size >= fileSizeLimit) {
      setError("이미지는 20MB 미만, 영상은 200MB 미만 파일만 올릴 수 있습니다.");
      setFile(null);
      return;
    }
    setError("");
    setFile(selectedFile);
    setImageUrl("");
    setAppliedImageUrl("");
  };

  const applyImageUrl = async () => {
    const normalizedUrl = imageUrl.trim();
    if (!/^https:\/\//i.test(normalizedUrl)) {
      setAppliedImageUrl("");
      setError("HTTPS 이미지 URL을 입력해 주세요.");
      return;
    }
    setIsApplyingImageUrl(true);
    setError("");
    try {
      await new Promise<void>((resolve, reject) => {
        const previewImage = new Image();
        previewImage.referrerPolicy = "no-referrer";
        previewImage.addEventListener("load", () => resolve(), {once: true});
        previewImage.addEventListener("error", () => reject(new Error("Image not found")), {once: true});
        previewImage.src = normalizedUrl;
      });
      setFile(null);
      setAppliedImageUrl(normalizedUrl);
      setStatus("이미지를 확인했습니다. 등록하면 Cloudflare에 복사됩니다.");
    } catch {
      setAppliedImageUrl("");
      setError("입력한 URL에서 이미지를 찾을 수 없습니다.");
    } finally {
      setIsApplyingImageUrl(false);
    }
  };

  const uploadImage = async (itemId: string, selectedFile: File) => {
    const target = await createAdminCloudflareR2Upload(
      itemId,
      selectedFile.name,
      selectedFile.type,
      selectedFile.size
    );
    await new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("PUT", target.uploadURL);
      request.setRequestHeader("Content-Type", selectedFile.type);
      request.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
      request.addEventListener("load", () => {
        if (request.status >= 200 && request.status < 300) {
          resolve();
          return;
        }
        reject(new Error("Cloudflare R2 이미지 업로드에 실패했습니다."));
      });
      request.addEventListener("error", () => {
        reject(new Error("Cloudflare R2에 연결할 수 없습니다."));
      });
      request.send(selectedFile);
    });
    return target.objectKey;
  };

  const uploadVideo = async (selectedFile: File) => {
    const target = await createAdminCloudflareStreamUpload(
      selectedFile.name,
      selectedFile.type,
      selectedFile.size
    );
    await new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("POST", target.uploadURL);
      request.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
      request.addEventListener("load", () => {
        if (request.status >= 200 && request.status < 300) {
          resolve();
          return;
        }
        reject(new Error("Cloudflare Stream 영상 업로드에 실패했습니다."));
      });
      request.addEventListener("error", () => {
        reject(new Error("Cloudflare Stream에 연결할 수 없습니다."));
      });
      const body = new FormData();
      body.append("file", selectedFile);
      request.send(body);
    });
    return target;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) {
      return;
    }
    setError("");
    setStatus("");

    const tags = tagInput
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    if (form.title.trim().length < 2 || form.description.trim().length < 20) {
      setError("제목과 20자 이상의 설명을 입력해 주세요.");
      return;
    }
    const fileSizeLimit = file?.type.startsWith("image/")
      ? MAX_IMAGE_FILE_SIZE
      : MAX_VIDEO_FILE_SIZE;
    if (file && (file.size >= fileSizeLimit || !ALLOWED_FILE_TYPES.test(file.type))) {
      setError("이미지는 20MB 미만, 영상은 200MB 미만 파일만 올릴 수 있습니다.");
      return;
    }
    const normalizedImageUrl = imageUrl.trim();

    setSaving(true);
    try {
      const initial = await saveAdminBlogKnowledge({
        ...form,
        tags,
        imageUrl: normalizedImageUrl || undefined,
        publicMedia: form.approvedForBlog && form.publicMedia,
      });
      let savedForm: BlogKnowledgeInput = {
        ...form,
        id: initial.id,
        tags,
        mediaPath: initial.mediaPath,
        mediaUrl: initial.mediaUrl,
        mediaName: initial.mediaName,
        mediaContentType: initial.mediaContentType,
      };

      if (file && !normalizedImageUrl && !form.mediaPath && !form.streamUid) {
        if (file.type.startsWith("video/")) {
          const video = await uploadVideo(file);
          savedForm = {
            ...savedForm,
            materialType: "media",
            streamUid: video.uid,
            streamUrl: video.streamUrl,
            mediaName: file.name,
            mediaContentType: file.type,
          };
        } else {
          const uploadedPath = await uploadImage(initial.id, file);
          savedForm = {
            ...savedForm,
            materialType: "media",
            mediaPath: uploadedPath,
            mediaName: file.name,
            mediaContentType: file.type,
          };
        }
        const savedMedia = await saveAdminBlogKnowledge(savedForm);
        savedForm = {
          ...savedForm,
          mediaPath: savedMedia.mediaPath,
          mediaUrl: savedMedia.mediaUrl,
          mediaName: savedMedia.mediaName,
          mediaContentType: savedMedia.mediaContentType,
        };
      }

      setStatus("지식 자료를 저장했습니다.");
      setForm(savedForm);
      setFile(null);
      setImageUrl("");
      setAppliedImageUrl("");
      await loadItemsPage(0, null);
    } catch (saveError: any) {
      setError(saveError?.message ?? "지식 자료를 저장하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || saving) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await deleteAdminBlogKnowledge(deleteTarget.id);
      setDeleteTarget(null);
      startNew();
      await loadItemsPage(0, null);
      setStatus(
        result.retainedMedia
          ? "지식 자료를 삭제했습니다. 블로그에서 사용하는 이미지는 보존했습니다."
          : "지식 자료와 사용하지 않는 원본 파일을 삭제했습니다."
      );
    } catch (deleteError: any) {
      setError(deleteError?.message ?? "지식 자료를 삭제하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const canGoPreviousPage = !loading && pageIndex > 0;
  const nextPageCursor = pageCursors[pageIndex + 1] ?? null;
  const canGoNextPage = !loading && hasMore && nextPageCursor !== null;

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>AUTOVID 지식 저장소</h1>
        <p className={styles.error}>관리자 권한이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>AUTOVID 지식 저장소</h1>
        <p className={styles.lead}>
          제품 정보와 미디어 설명을 보관하고, 승인한 자료만 블로그 자동 작성의 근거로 제공합니다.
        </p>
      </header>

      {status && <p className={styles.status} role="status">{status}</p>}
      {error && <p className={styles.error} role="alert">{error}</p>}

      <div className={styles.layout}>
        <section className={styles.panel} aria-labelledby="knowledge-list-title">
          <div className={styles.actions}>
            <h2 className={styles.panelTitle} id="knowledge-list-title">저장된 자료</h2>
            <button type="button" className={styles.button} onClick={startNew}>새 자료</button>
          </div>
          <nav className={styles.pageRow} aria-label="지식 항목 페이지">
            <button
              type="button"
              className={styles.pageButton}
              disabled={!canGoPreviousPage}
              onClick={() => {
                const previousPage = pageIndex - 1;
                const previousCursor = pageCursors[previousPage] ?? null;
                void loadItemsPage(previousPage, previousCursor);
              }}
            >
              이전
            </button>
            <span className={styles.pageText}>{pageIndex + 1} 페이지 · {KNOWLEDGE_PAGE_SIZE}개씩</span>
            <button
              type="button"
              className={styles.pageButton}
              disabled={!canGoNextPage}
              onClick={() => {
                if (nextPageCursor) {
                  void loadItemsPage(pageIndex + 1, nextPageCursor);
                }
              }}
            >
              다음
            </button>
          </nav>
          <ul className={styles.itemList}>
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`${styles.itemButton} ${form.id === item.id ? styles.itemButtonActive : ""}`}
                  title={item.title}
                  onClick={() => selectItem(item)}
                >
                  <span className={styles.itemTitle}>{item.title}</span>
                  <span className={styles.itemMeta}>
                    {item.materialType === "media" ? "미디어" : "정보"}
                    {item.approvedForBlog ? " · 블로그 승인" : " · 승인 전"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {loading && <p className={styles.hint}>불러오는 중...</p>}
          {!loading && items.length === 0 && <p className={styles.hint}>등록된 자료가 없습니다.</p>}
        </section>

        <section className={styles.panel} aria-labelledby="knowledge-form-title">
          <h2 className={styles.panelTitle} id="knowledge-form-title">
            {form.id ? "자료 수정" : "자료 등록"}
          </h2>
          <form className={styles.form} noValidate onSubmit={handleSubmit}>
            <div className={styles.field}>
              <label htmlFor="knowledge-title">제목</label>
              <input id="knowledge-title" className={styles.input} value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
            </div>
            <div className={styles.field}>
              <label htmlFor="knowledge-description">설명과 블로그에 활용할 사실</label>
              <textarea id="knowledge-description" className={styles.textarea} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
              <span className={styles.hint}>미디어의 화면 내용, 기능, 사용 장면과 과장하면 안 되는 조건을 구체적으로 적어 주세요.</span>
            </div>
            <div className={styles.field}>
              <label htmlFor="knowledge-tags">태그</label>
              <input id="knowledge-tags" className={styles.input} value={tagInput} onChange={(event) => setTagInput(event.target.value)} placeholder="자막, 마케팅 영상, 이미지 모션" />
            </div>
            <div className={styles.field}>
              <label htmlFor="knowledge-source-label">출처 이름</label>
              <input id="knowledge-source-label" className={styles.input} value={form.sourceLabel} onChange={(event) => setForm({ ...form, sourceLabel: event.target.value })} />
            </div>
            <div className={styles.field}>
              <label htmlFor="knowledge-source-url">출처 URL</label>
              <input id="knowledge-source-url" type="url" className={styles.input} value={form.sourceUrl} onChange={(event) => setForm({ ...form, sourceUrl: event.target.value })} placeholder="https://" />
            </div>
            <div className={styles.field}>
              <label htmlFor="knowledge-type">자료 유형</label>
              <select id="knowledge-type" className={styles.select} value={form.materialType} onChange={(event) => setForm({ ...form, materialType: event.target.value as "information" | "media" })}>
                <option value="information">텍스트 정보</option>
                <option value="media">미디어</option>
              </select>
            </div>
            {form.materialType === "media" && (
              <div className={styles.mediaFields}>
                {(localPreviewUrl || appliedImageUrl || form.mediaUrl) && (
                  <figure className={styles.previewFrame}>
                    <img
                      className={styles.previewImage}
                      src={localPreviewUrl || appliedImageUrl || form.mediaUrl}
                      alt="등록할 지식 이미지 미리보기"
                    />
                    <figcaption className={styles.hint}>
                      {file?.name || form.mediaName || "URL 이미지"}
                    </figcaption>
                  </figure>
                )}

                {!form.mediaPath && !form.streamUid && (
                  <div className={styles.field}>
                    <label htmlFor="knowledge-image-url">이미지 URL</label>
                    <div className={styles.urlRow}>
                      <input
                        id="knowledge-image-url"
                        type="url"
                        className={styles.input}
                        value={imageUrl}
                        aria-describedby="knowledge-image-url-help"
                        onChange={(event) => {
                          setImageUrl(event.target.value);
                          setAppliedImageUrl("");
                          setStatus("");
                          if (event.target.value.trim()) {
                            setFile(null);
                          }
                        }}
                        placeholder="https://example.com/image.webp"
                      />
                      <button
                        type="button"
                        className={styles.button}
                        disabled={isApplyingImageUrl || !imageUrl.trim()}
                        onClick={() => void applyImageUrl()}
                      >
                        {isApplyingImageUrl ? "확인 중..." : "적용"}
                      </button>
                    </div>
                    <span className={styles.hint} id="knowledge-image-url-help">
                      적용하면 미리보기가 나타납니다. 등록할 때 원본을 다시 확인하고 Cloudflare에 복사합니다.
                    </span>
                  </div>
                )}

                {!form.mediaPath && !form.streamUid && (
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>로컬 파일</span>
                    <div
                      className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ""}`}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={() => setIsDragOver(false)}
                      onDrop={(event) => {
                        event.preventDefault();
                        setIsDragOver(false);
                        selectLocalFile(event.dataTransfer.files?.[0] ?? null);
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        id="knowledge-file"
                        type="file"
                        className={styles.visuallyHidden}
                        accept="image/*,video/*"
                        onChange={(event) => selectLocalFile(event.target.files?.[0] ?? null)}
                      />
                      <p className={styles.dropTitle}>이미지 또는 영상을 여기에 드래그하세요</p>
                      <p className={styles.hint}>이미지 20MB 미만 · 영상 200MB 미만</p>
                      <button
                        type="button"
                        className={styles.button}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        파일 찾기
                      </button>
                    </div>
                    {file && (
                      <div className={styles.fileSummary} role="status">
                        <span>{file.name}</span>
                        <button type="button" className={styles.removeButton} onClick={() => setFile(null)}>
                          선택 취소
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {(form.mediaPath || form.streamUid) && (
                  <p className={styles.hint}>Cloudflare 저장 완료: {form.mediaName}</p>
                )}
                {saving && file && (
                  <progress className={styles.progress} max={100} value={uploadProgress}>
                    {uploadProgress}%
                  </progress>
                )}
              </div>
            )}
            <fieldset className={styles.checks}>
              <legend className={styles.legend}>활용 범위</legend>
              <label className={styles.checkLabel}>
                <input type="checkbox" checked={form.approvedForBlog} onChange={(event) => setForm({ ...form, approvedForBlog: event.target.checked, publicMedia: event.target.checked ? form.publicMedia : false })} />
                설명과 태그를 블로그 자동 작성 근거로 사용 승인
              </label>
              <label className={styles.checkLabel}>
                <input type="checkbox" checked={form.publicMedia} disabled={!form.approvedForBlog || (!form.mediaPath && !form.streamUid && !file && !imageUrl.trim())} onChange={(event) => setForm({ ...form, publicMedia: event.target.checked })} />
                첨부한 이미지·영상도 공개 블로그에서 사용 허용
              </label>
            </fieldset>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.button} ${styles.buttonPrimary}`} disabled={saving}>
                {saving ? "저장 중..." : "저장"}
              </button>
              {form.id && (
                <button type="button" className={`${styles.button} ${styles.buttonDanger}`} disabled={saving} onClick={() => setDeleteTarget(items.find((item) => item.id === form.id) ?? null)}>
                  삭제
                </button>
              )}
            </div>
          </form>

          {deleteTarget && (
            <div className={styles.confirmBox} role="alertdialog" aria-labelledby="delete-knowledge-title" aria-describedby="delete-knowledge-description">
              <h3 id="delete-knowledge-title">이 자료를 삭제할까요?</h3>
              <p id="delete-knowledge-description">
                {deleteTarget.title} 자료를 삭제합니다. 블로그 글에서 사용 중인 이미지는 링크가 깨지지 않도록 Cloudflare에 보존합니다.
              </p>
              <div className={styles.actions}>
                <button type="button" className={styles.button} onClick={() => setDeleteTarget(null)}>취소</button>
                <button type="button" className={`${styles.button} ${styles.buttonDanger}`} onClick={() => void confirmDelete()}>자료 삭제</button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminKnowledgePage;
